<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import api from '@/api';
import { SHAREPOINT_ERROR_MESSAGES } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';

interface Props {
  isDisabled: boolean;
}
const props = defineProps<Props>();

// このコンポーネントはDgrDialogをラップしているだけなので、openの値をこのコンポーネント内で変更しない。
const open = defineModel<boolean>('open', { default: false });
watch(
  () => open.value,
  () => {
    if (!open.value) {
      thumbprint.value = '';
      emit('onClose');
    }
  },
);

const { createSnackbar } = useSnackbar();

const thumbprint = ref('');
const currentThumbprint = ref();

const isCreating = ref(false);
const isCreatable = computed(() => {
  return thumbprint.value !== '' && !props.isDisabled;
});

const emit = defineEmits<{
  onClose: [void];
  onUpdated: [void];
}>();

const updateSharepointCredential = async () => {
  isCreating.value = true;
  try {
    await api.putSharepointCredential({
      thumbprint: thumbprint.value,
    });
    createSnackbar({
      message: '認証情報を更新しました',
      type: 'success',
    });
    emit('onUpdated');
    open.value = false;
  } catch (error) {
    let errorMessage = '認証情報の登録に失敗しました';
    if (isAxiosError(error)) {
      const serverError: string = error.response?.data.error;
      if (serverError in SHAREPOINT_ERROR_MESSAGES) {
        errorMessage = SHAREPOINT_ERROR_MESSAGES[serverError];
      }
    }
    createSnackbar({
      message: errorMessage,
      type: 'error',
    });

    // eslint-disable-next-line no-console
    console.error(error);
  }
  isCreating.value = false;
};

onMounted(async () => {
  const response = await api.getSharepointCredential();
  currentThumbprint.value = response?.thumbprint;
});
</script>

<template>
  <DgrDialog v-model:open="open" title="ストレージの認証情報編集">
    <template #description>
      <div class="modal-body">
        <div class="modal-form">
          <label class="c-title-bold--m" for="thumbprint-form"
            >証明書サムプリント</label
          >

          <div class="c-text-regular--s current-thumbprint">
            {{ currentThumbprint }}
          </div>

          <div>
            <input
              type="text"
              maxlength="40"
              v-model="thumbprint"
              id="thumbprint-form"
            />
            <div class="text-warning c-text-regular--s" v-if="isDisabled">
              現在は認証情報を変更できません
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #submit>
      <LoadableButton
        class="modal-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: !isCreatable }"
        :disabled="!isCreatable"
        @click="updateSharepointCredential"
        :is-loading="isCreating"
      >
        保存
      </LoadableButton>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.modal-body {
  width: 568px; // 600 - padding * 2
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  .modal-form {
    padding: 9px 8px 0px 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    .current-client-secret,
    .current-thumbprint {
      color: $color-gray800;
    }
    input {
      width: 500px;
    }
    .text-warning {
      margin-top: 25px;
      color: $color-orange1000;
    }
  }
}
</style>
